import { useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import {
  Badge,
  Flex,
  Group,
  Paper,
  Stack,
  Stepper,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import MainTable from "../../../../layouts/Table/MainTable";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import NoItemsModal from "../../../../components/TableComponents/NoItemsModal";
import { parseDateFuture } from "../../../../functions/dates";
import { getReferredUsers } from "../../../../api/UserAPI";
import AffiliationLink from "./AffiliationLink";
import { useProfileStore, useSessionStore } from "../../../../stores/UserStore";
import { IconGift, IconSend, IconUserPlus } from "@tabler/icons-react";

const AffiliationPage = () => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);

  const reseller = useSessionStore((s) => s.reseller);
  const profile = useProfileStore((s) => s.profile);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const columns = useMemo(
    () => [
      numberColumn(),
      nameColumn({
        field: "first_name",
        headerName: lang.users_list.columns.first_name,
        width: 200,
      }),
      {
        field: "last_name",
        headerName: lang.users_list.columns.last_name,
        width: 175,
      },
      {
        field: "email",
        headerName: lang.users_list.columns.email,
        width: 250,
      },
      {
        field: "subscriptions",
        headerName: lang.users_list.columns.subscription,
        width: 125,
        type: "options",
        disabledCondition: true,
        valueGetter: ({ value }) => {
          return value?.active ? "active" : "not_active";
        },
        options: [
          {
            label: lang.referral.active,
            value: "active",
          },
          {
            label: lang.referral.not_active,
            value: "not_active",
          },
        ],
        renderCell: ({ row }) => {
          const value = row.subscriptions;
          return value?.active && !value?.trial ? (
            <Tooltip
              label={`${lang.referral.end_date} ${parseDateFuture(
                value?.end_date
              )}`}
            >
              <Badge color={"tertiary.8"} size="xs">
                {lang.referral.active}
              </Badge>
            </Tooltip>
          ) : (
            <Tooltip
              label={
                value?.trial
                  ? `${lang.referral.trial} ${parseDateFuture(value?.end_date)}`
                  : lang.referral.free_version
              }
            >
              <Badge color={"red"} size="xs">
                {lang.referral.not_active}{" "}
              </Badge>
            </Tooltip>
          );
        },
      },
      {
        field: "monthly_amount",
        headerName: lang.settings.amount,
        valueGetter: ({ row }) => {
          return row?.subscriptions?.monthly_amount || 0;
        },
        renderCell: ({ value }) => `${value}€ / mois`,
      },
      dateColumn(),
    ],
    [lang]
  );

  useListingLoader({
    loading,
    setLoading,
    listGetter: getReferredUsers,
    responseField: "users_list",
    setRows,
  });

  return (
    <Stack gap={0} pr={48} pt={16} h={"calc(100vh - 96px)"}>
      <Group gap={"md"} mb={24}>
        <Text size="xl" fw={600} mr={"auto"}>
          {lang.settings.links.referrals}
        </Text>

        <AffiliationLink />
      </Group>

      <Paper component={Stack} p={"lg"} gap={"xs"}>
        <Text size="xl" fw={600} c={"primary"}>
          {lang.settings.ref_text}
        </Text>

        <Text size="md">
          {lang.referral.left.text
            .replaceAll("#", reseller?.name)
            .replaceAll("$", profile?.first_name || "-")}
        </Text>

        <Stepper
          mt={16}
          flex={1}
          styles={{
            stepDescription: {
              marginTop: 10,
            },
          }}
          wrap={false}
        >
          <Stepper.Step
            icon={
              <ThemeIcon radius={"md"} size={"xl"} variant="light">
                <IconSend size={18} />
              </ThemeIcon>
            }
            styles={{
              stepLabel: {
                fontSize: 16,
              },
              stepIcon: {
                borderRadius: 0,
                background: "none",
                border: 0,
              },
            }}
            label={"Invitation"}
            // label={lang.referral.left.step1}
            description={lang.settings.ref_step1}
            w={"28%"}
          />
          <Stepper.Step
            icon={
              <ThemeIcon
                radius={"md"}
                size={"xl"}
                variant="light"
                color="green"
              >
                <IconUserPlus size={18} />
              </ThemeIcon>
            }
            styles={{
              stepLabel: {
                fontSize: 16,
              },
              stepIcon: {
                borderRadius: 0,
                background: "none",
                border: 0,
              },
            }}
            label={lang.referral.left.step2}
            description={lang.settings.ref_step2}
            w={"28%"}
          />
          <Stepper.Step
            icon={
              <ThemeIcon
                radius={"md"}
                size={"xl"}
                variant="light"
                color="#fcfbff"
                bg={"green.4"}
              >
                <IconGift size={18} />
              </ThemeIcon>
            }
            styles={{
              stepLabel: {
                fontSize: 16,
              },
              stepIcon: {
                borderRadius: 0,
                background: "none",
                border: 0,
              },
            }}
            label={"Commission"}
            // label={lang.referral.left.step3}
            description={lang.settings.ref_step3}
            w={"28%"}
          />
        </Stepper>
      </Paper>

      <Text fw={600} size="lg" mt={24}>
        {lang.settings.your_referrals}
      </Text>

      <Flex flex={1} pos={"relative"} mt={24}>
        <NoItemsModal
          rows={rows}
          loading={loading}
          text={<Text size="xl"> {lang.settings.no_referral}</Text>}
        />
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={loading}
          setLoading={setLoading}
          id={"list-table"}
          whiteButtons
          disableCheckboxes
          disableFilter
          disableGotoPage
          disableSearch
          disablePerPage
          disableVisibleColumns
          tableSx={{
            "[data-field='first_name']": {
              paddingLeft: 3,
            },
            ".table-toolbar": {
              display: "none",
            },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default AffiliationPage;
